/**
 * =============================================================================
 * conditions-evaluator.js
 * =============================================================================
 * =============================================================================
 */

class ConditionsEvaluator {
  rule;
  report = [];

  // Dynamically-callable comparator functions
  comparatorFunctions = {
    "gte": (a, b) => a >= b,
    "gt" : (a, b) => a > b,
    "lte": (a, b) => a <= b,
    "lt" : (a, b) => a < b,
    "eq" : (a, b) => a == b
  }

  constructor(rule) {
    this.rule = rule;
    this.conditionEvaluators = {
      "cart.attributes": this.#evaluateCartAttributesCondition.bind(this),
      "cart.totalExcludingGiftCards": this.#evaluateCartTotalExcludingGiftCardsCondition.bind(this),
      "cart.excludeSelf": this.#evaluateCartExcludeSelfCondition.bind(this),
      "cart.contains": this.#evaluateCartContainsCondition.bind(this),

      "product.tags": this.#evaluateProductTagsCondition.bind(this),

      // Add more condition evaluators here...
    };
  }

  /**
   * Evaluate where a given rule's conditions are met based on the trigger event
   * detail.
   * 
   * @param {Object} eventDetail 
   * @returns {Boolean} Whether the rule conditions are met
   */
  async evaluate(eventDetail) {
    let meetsConditions = true;
    this.report = [];

    for (const [conditionGroup, conditions] of Object.entries(this.rule.conditions)) {
      for (const [conditionCategory, condition] of Object.entries(conditions)) {
        const conditionEvaluator = this.conditionEvaluators[`${conditionGroup}.${conditionCategory}`];

        if (conditionEvaluator) {
          let meetsCondition = await conditionEvaluator(condition, eventDetail);
          meetsConditions = meetsConditions && meetsCondition;
        } else {
          console.warn("[P-C-E] No evaluator strategy found", `${conditionGroup}.${conditionCategory}`);
        }
      }
    }

    return meetsConditions;
  }

  //
  // 'Cart' condition strategies
  //

  /**
   * Evaluate condition based on whether the cart contains specific variants or 
   * products.
   * 
   * @param {Object} condition 
   * @param {Object} eventDetail 
   * @returns {Boolean} Whether the condition is met
   */
  #evaluateCartContainsCondition(condition, eventDetail) {
    let meetsCondition = true;
    let variants = condition.variants ? Array.isArray(condition.variants) ? condition.variants : [condition.variants] : [];
    let products = condition.products ? Array.isArray(condition.products) ? condition.products : [condition.products] : [];
    let doesContain = eventDetail.cart.some(item => {
      return variants.includes(item.id) || products.includes(item.product_id);
    });

    if(!doesContain){
      meetsConditions = false;
    }

    this.report.push({ step: "cart", condition: "contains", result: doesContain ? "✅" : "❌", target: "In cart", actual: doesContain ? "In cart" : "Not in cart" });
    
    return meetsCondition;
  }

  /**
   * Evaluate condition based on whether the cart already contains the target
   * variant, usually to prevent infinite loops of adding the same product.
   * 
   * @param {Object} condition 
   * @param {Object} eventDetail 
   * @returns {Boolean} Whether the condition is met
   */
  #evaluateCartExcludeSelfCondition(condition, eventDetail) {
    let meetsCondition = true;

    // If condition is 'excludeSelf: false', we DON'T want to apply this condition
    if(!condition){
      return;
    }

    // Check if our serialized variant is already in the cart by serializing each item in the cart and comparing
    let serializedVariant = JSON.stringify({ id: this.rule.variant.id, properties: this.rule.variant.properties || {}});
    let alreadyHasVariant = eventDetail.cart.some(item => {
      let serializedExistingLineItem = JSON.stringify({ id: item.id, properties: item.properties });
      return serializedExistingLineItem === serializedVariant
    });

    if(alreadyHasVariant){
      meetsCondition = false;
    }

    this.report.push({ step: "cart", condition: "excludeSelf", result: !alreadyHasVariant ? "✅" : "❌", target: "Not in cart", actual: alreadyHasVariant ? "Already in cart" : "Not in cart" });
  
    return meetsCondition;
  }

  /**
   * Evaluate condition based on the total cart value excluding gift cards
   * 
   * @param {Object} condition 
   * @param {Object} eventDetail 
   * @returns {Boolean} Whether the condition is met
   */
  #evaluateCartTotalExcludingGiftCardsCondition(condition, eventDetail) {
    let meetsCondition = true;

    let totalExcludingGiftCards = eventDetail.cart.reduce((previousValue, item) => {
      if(item.gift_card === true){
        return previousValue;
      } else {
        return previousValue + item.final_line_price
      }
    }, 0);

    let result = this.comparatorFunctions[condition.comparator](totalExcludingGiftCards, condition.value)
    if (!result){
      meetsConditions = false;
    }

    this.report.push({ step: "cart", condition: "totalExcludingGiftCards", result: result ? "✅" : "❌", target: condition.value, actual: totalExcludingGiftCards, comparator: condition.comparator });
    
    return meetsConditions; 
  }

  /**
   * Evaluate condition based on whether the cart contains specific attributes
   * 
   * @param {Object} condition 
   * @param {Object} eventDetail 
   * @returns {Boolean} Whether the condition is met
   */
  #evaluateCartAttributesCondition(condition, eventDetail) {
    let meetsCondition = true;

    condition.forEach(attributeCondition => {
      let result = eventDetail.response.attributes[attributeCondition.key].toString() == attributeCondition.value.toString()
      if(!result){
        meetsConditions = false;
      }
      this.report.push({ step: "cart", condition: "attributes", result: result ? "✅" : "❌", target: `${attributeCondition.key}=${attributeCondition.value}`, actual: `${attributeCondition.key}=${eventDetail.response.attributes[attributeCondition.key]}` });
    });

    return meetsCondition;
  }

  //
  // 'Product' condition strategies
  //

  /**
   * Evaluate condition based on whether a given product has specific tags
   * 
   * @param {Object} condition 
   * @returns {Promise<boolean>} Whether the condition is met
   */
  async #evaluateProductTagsCondition(condition) {
    let meetsCondition = true;
    let ids = condition.map(tagCondition => tagCondition.id);
    // @ts-ignore
    let results = await MP.Utilities.getProductJson(ids);
    condition.forEach(tagCondition => {
      let product = results.find(result => result.id == tagCondition.id);
      if(product){
        meetsCondition = meetsCondition && product.tags.includes(tagCondition.value);
      } else {
        meetsCondition = false;
      }
      this.report.push({ step: "product", condition: "tags", result: product?.tags.includes(tagCondition.value) ? "✅" : "❌", target: tagCondition.value, actual: product?.tags.join(", ") || "No product found", comparator: "includes" });
    });

    return meetsCondition;
  }
}

export default ConditionsEvaluator;